import React, { useEffect, useMemo, useState } from "react";
import {
    ClipboardCheck,
    Award,
    Plus,
    Trash2,
    Trophy,
    Calendar as CalendarIcon,
    Clock,
    Building2,
    Coins,
    CheckCircle2,
    ChevronLeft,
    Sparkles,
    Bot,
    ListChecks,
    Target,
    Loader2,
    FileText,
    Edit3,
    AlertCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
    listAwardedTenders,
    markTenderAwarded,
    unmarkTenderAwarded,
    getTracking,
    createCommitment,
    updateCommitment,
    deleteCommitment,
    aiExtractCommitments,
    createTrackingTask,
    updateTrackingTask,
    deleteTrackingTask,
    addTrackingNote,
    deleteTrackingNote,
} from "@/lib/api";
import { listTenders } from "@/lib/api";
import { Layout } from "@/components/Layout";

const COMMITMENT_TYPES = [
    { value: "bolsa_horas", label: "Bolsa de horas", color: "violet" },
    { value: "garantia", label: "Garantía", color: "emerald" },
    { value: "visitas", label: "Visitas", color: "cyan" },
    { value: "mejora", label: "Mejora", color: "amber" },
    { value: "plazo", label: "Plazo", color: "rose" },
    { value: "sla", label: "SLA", color: "sky" },
    { value: "seguro", label: "Seguro", color: "slate" },
    { value: "formacion", label: "Formación", color: "indigo" },
    { value: "residuos", label: "Residuos", color: "lime" },
    { value: "otro", label: "Otro", color: "slate" },
];

const STATUS_META = {
    pending: { label: "Pendiente", color: "bg-amber-100 text-amber-800" },
    in_progress: { label: "En curso", color: "bg-sky-100 text-sky-800" },
    done: { label: "Cumplido", color: "bg-emerald-100 text-emerald-800" },
    overdue: { label: "Vencido", color: "bg-rose-100 text-rose-800" },
};

export default function Seguimiento() {
    const [awarded, setAwarded] = useState([]);
    const [allTenders, setAllTenders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedId, setSelectedId] = useState(null);
    const [showMarkDlg, setShowMarkDlg] = useState(false);

    useEffect(() => {
        document.title = "Seguimiento · LicitaIA";
        loadAll();
    }, []);

    const loadAll = async () => {
        setLoading(true);
        try {
            const [a, t] = await Promise.all([listAwardedTenders(), listTenders()]);
            setAwarded(a.awarded || []);
            const list = Array.isArray(t) ? t : t.tenders || t.items || [];
            setAllTenders(list);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error cargando seguimiento");
        } finally {
            setLoading(false);
        }
    };

    if (selectedId) {
        return (
            <Layout>
                <TrackingDetail
                    tenderId={selectedId}
                    onBack={() => {
                        setSelectedId(null);
                        loadAll();
                    }}
                />
            </Layout>
        );
    }

    const totalAmount = awarded.reduce(
        (s, x) => s + (Number(x.awarded_amount) || 0),
        0,
    );
    const totalCommitments = awarded.reduce(
        (s, x) => s + (x.commitments_count || 0),
        0,
    );
    const totalTasksPending = awarded.reduce(
        (s, x) => s + (x.tasks_pending || 0),
        0,
    );
    const candidates = allTenders.filter(
        (t) => !awarded.some((a) => a.id === t.id),
    );

    return (
        <Layout>
            <div className="p-6 md:p-8 space-y-6" data-testid="seguimiento-page">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                <div>
                    <div className="text-xs font-bold uppercase tracking-[0.18em] text-brand-600 mb-1">
                        Post-adjudicación
                    </div>
                    <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight text-ink-950 flex items-center gap-3">
                        <ClipboardCheck size={28} className="text-brand-600" />
                        Seguimiento de licitaciones
                    </h1>
                    <p className="text-ink-500 text-sm mt-1 max-w-2xl">
                        Controla todo lo que comprometiste en oferta: bolsa de horas,
                        garantías, visitas, plazos y mejoras. Crea tareas con fecha que se
                        sincronizan con el calendario.
                    </p>
                </div>
                <Button
                    onClick={() => setShowMarkDlg(true)}
                    className="rounded-full bg-brand-600 hover:bg-brand-700 text-white px-6 h-11 font-bold shadow-pop"
                    data-testid="mark-awarded-btn"
                >
                    <Trophy size={16} className="mr-2" />
                    Marcar como adjudicada
                </Button>
            </div>

            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Kpi
                    icon={Award}
                    label="Adjudicadas"
                    value={awarded.length}
                    color="from-brand-600 to-cyan-500"
                />
                <Kpi
                    icon={Coins}
                    label="Volumen adjudicado"
                    value={`${totalAmount.toLocaleString("es-ES", {
                        maximumFractionDigits: 0,
                    })} €`}
                    color="from-emerald-500 to-cyan-500"
                />
                <Kpi
                    icon={ListChecks}
                    label="Compromisos activos"
                    value={totalCommitments}
                    color="from-violet-500 to-brand-500"
                />
                <Kpi
                    icon={CalendarIcon}
                    label="Tareas pendientes"
                    value={totalTasksPending}
                    color="from-amber-500 to-rose-500"
                />
            </div>

            {/* List */}
            <div className="bg-white rounded-3xl border border-ink-100 shadow-pop overflow-hidden">
                {loading ? (
                    <div className="p-12 text-center text-ink-400">
                        <Loader2 size={28} className="animate-spin mx-auto mb-3" />
                        Cargando…
                    </div>
                ) : awarded.length === 0 ? (
                    <div className="p-12 text-center">
                        <Award size={36} className="mx-auto text-ink-300 mb-3" />
                        <div className="font-display font-bold text-ink-700">
                            Aún no hay licitaciones adjudicadas
                        </div>
                        <p className="text-sm text-ink-500 mt-1 mb-5">
                            Cuando ganes una, márcala desde aquí y empieza el seguimiento.
                        </p>
                        <Button
                            onClick={() => setShowMarkDlg(true)}
                            className="rounded-full bg-brand-600 hover:bg-brand-700 text-white px-6 h-11 font-bold"
                            data-testid="empty-mark-awarded-btn"
                        >
                            <Trophy size={16} className="mr-2" />
                            Marcar primera adjudicación
                        </Button>
                    </div>
                ) : (
                    <div className="divide-y divide-ink-100">
                        {awarded.map((t) => (
                            <button
                                key={t.id}
                                type="button"
                                onClick={() => setSelectedId(t.id)}
                                className="w-full text-left p-5 hover:bg-brand-50/30 transition-colors flex items-center gap-4"
                                data-testid={`awarded-row-${t.id}`}
                            >
                                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-cyan-500 text-white flex items-center justify-center shadow-pop shrink-0">
                                    <Trophy size={18} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="font-bold text-ink-950 truncate">
                                        {t.title}
                                    </div>
                                    <div className="text-xs text-ink-500 mt-0.5 flex flex-wrap gap-3">
                                        {t.contracting_authority && (
                                            <span className="truncate max-w-[280px]">
                                                <Building2 size={11} className="inline mr-0.5" />
                                                {t.contracting_authority}
                                            </span>
                                        )}
                                        {t.kickoff_date && (
                                            <span>
                                                <CalendarIcon
                                                    size={11}
                                                    className="inline mr-0.5"
                                                />
                                                Inicio {fmt(t.kickoff_date)}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="hidden md:flex items-center gap-5 text-xs shrink-0">
                                    <div className="text-right">
                                        <div className="font-mono font-extrabold text-emerald-700 text-lg leading-none">
                                            {(Number(t.awarded_amount) || 0).toLocaleString(
                                                "es-ES",
                                                { maximumFractionDigits: 0 },
                                            )}{" "}
                                            €
                                        </div>
                                        <div className="text-[10px] uppercase tracking-wider text-ink-400 font-bold">
                                            Adjudicado
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className="font-mono font-bold text-brand-700">
                                            {t.commitments_count || 0}
                                        </div>
                                        <div className="text-[10px] uppercase tracking-wider text-ink-400 font-bold">
                                            Compromisos
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className="font-mono font-bold text-amber-700">
                                            {t.tasks_pending || 0}
                                        </div>
                                        <div className="text-[10px] uppercase tracking-wider text-ink-400 font-bold">
                                            Tareas
                                        </div>
                                    </div>
                                </div>
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {showMarkDlg && (
                <MarkAwardedDialog
                    candidates={candidates}
                    onClose={() => setShowMarkDlg(false)}
                    onMarked={(id) => {
                        setShowMarkDlg(false);
                        setSelectedId(id);
                    }}
                />
            )}
        </div>
        </Layout>
    );
}

function Kpi({ icon: Icon, label, value, color }) {
    return (
        <div className="bg-white rounded-2xl p-4 border border-ink-100 shadow-pop">
            <div
                className={`w-10 h-10 rounded-xl bg-gradient-to-br ${color} text-white flex items-center justify-center shadow-pop mb-3`}
            >
                <Icon size={18} />
            </div>
            <div className="font-display text-2xl font-extrabold text-ink-950 leading-none">
                {value}
            </div>
            <div className="text-[11px] uppercase tracking-[0.14em] text-ink-400 font-bold mt-1">
                {label}
            </div>
        </div>
    );
}

function MarkAwardedDialog({ candidates, onClose, onMarked }) {
    const [tenderId, setTenderId] = useState("");
    const [form, setForm] = useState({
        awarded_amount: "",
        kickoff_date: "",
        contract_end_date: "",
        contract_reference: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        if (!tenderId) {
            toast.error("Selecciona una licitación");
            return;
        }
        setSubmitting(true);
        try {
            await markTenderAwarded(tenderId, {
                ...form,
                awarded_amount: form.awarded_amount
                    ? parseFloat(form.awarded_amount)
                    : undefined,
            });
            toast.success("Licitación marcada como adjudicada");
            onMarked(tenderId);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-xl rounded-3xl" data-testid="mark-awarded-dialog">
                <DialogHeader>
                    <div className="flex items-center gap-3 mb-1">
                        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-500 to-cyan-500 text-white flex items-center justify-center shadow-pop">
                            <Trophy size={18} />
                        </div>
                        <div>
                            <DialogTitle className="font-display text-2xl font-black">
                                Marcar como adjudicada
                            </DialogTitle>
                            <DialogDescription className="text-sm">
                                Selecciona la licitación ganada y registra los datos del
                                contrato.
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-4 mt-2">
                    <div>
                        <Label>Licitación</Label>
                        <select
                            value={tenderId}
                            onChange={(e) => setTenderId(e.target.value)}
                            className="w-full rounded-lg border border-ink-200 px-3 h-10 text-sm bg-white"
                            data-testid="mark-awarded-select"
                        >
                            <option value="">— Selecciona —</option>
                            {candidates.map((t) => (
                                <option key={t.id} value={t.id}>
                                    {t.title}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3">
                        <div>
                            <Label>Importe adjudicado (€)</Label>
                            <Input
                                type="number"
                                step="0.01"
                                value={form.awarded_amount}
                                onChange={update("awarded_amount")}
                                placeholder="248500"
                                data-testid="mark-amount"
                            />
                        </div>
                        <div>
                            <Label>Nº contrato / expediente</Label>
                            <Input
                                value={form.contract_reference}
                                onChange={update("contract_reference")}
                                placeholder="CO-2026-007"
                                className="font-mono"
                                data-testid="mark-ref"
                            />
                        </div>
                        <div>
                            <Label>Fecha inicio</Label>
                            <Input
                                type="date"
                                value={form.kickoff_date}
                                onChange={update("kickoff_date")}
                                data-testid="mark-kickoff"
                            />
                        </div>
                        <div>
                            <Label>Fecha fin contrato</Label>
                            <Input
                                type="date"
                                value={form.contract_end_date}
                                onChange={update("contract_end_date")}
                                data-testid="mark-end"
                            />
                        </div>
                    </div>
                    <DialogFooter className="gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={onClose}
                            className="rounded-full"
                        >
                            Cancelar
                        </Button>
                        <Button
                            type="submit"
                            disabled={submitting}
                            className="rounded-full bg-brand-600 hover:bg-brand-700 text-white font-bold"
                            data-testid="mark-awarded-submit"
                        >
                            {submitting ? (
                                <>
                                    <Loader2 size={14} className="animate-spin mr-2" />
                                    Guardando…
                                </>
                            ) : (
                                <>
                                    <Trophy size={14} className="mr-2" />
                                    Marcar y abrir seguimiento
                                </>
                            )}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

// ---------- Tracking detail ----------

function TrackingDetail({ tenderId, onBack }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [extracting, setExtracting] = useState(false);
    const [editing, setEditing] = useState(null); // commitment being edited
    const [taskEditing, setTaskEditing] = useState(null);
    const [noteText, setNoteText] = useState("");
    const [showAddCommitment, setShowAddCommitment] = useState(false);
    const [showAddTask, setShowAddTask] = useState(false);

    useEffect(() => {
        load();
    }, [tenderId]);

    const load = async () => {
        setLoading(true);
        try {
            const d = await getTracking(tenderId);
            setData(d);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error");
        } finally {
            setLoading(false);
        }
    };

    const handleAiExtract = async () => {
        setExtracting(true);
        try {
            const r = await aiExtractCommitments(tenderId);
            if (r.count === 0) {
                toast.warning(
                    "La IA no detectó compromisos cuantificables en el análisis. Añádelos a mano.",
                );
            } else {
                toast.success(`${r.count} compromisos extraídos por la IA`);
            }
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error IA");
        } finally {
            setExtracting(false);
        }
    };

    const handleAddNote = async () => {
        if (!noteText.trim()) return;
        try {
            await addTrackingNote(tenderId, noteText);
            setNoteText("");
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error");
        }
    };

    const handleDeleteNote = async (id) => {
        await deleteTrackingNote(id);
        await load();
    };

    const handleToggleStatus = async (c) => {
        const next =
            c.status === "done"
                ? "pending"
                : c.status === "pending"
                  ? "in_progress"
                  : "done";
        await updateCommitment(c.id, { status: next });
        await load();
    };

    const handleDeleteCommitment = async (id) => {
        await deleteCommitment(id);
        await load();
    };

    const handleToggleTask = async (t) => {
        const next =
            t.status === "done"
                ? "pending"
                : t.status === "pending"
                  ? "in_progress"
                  : "done";
        await updateTrackingTask(t.id, { status: next });
        await load();
    };

    const handleDeleteTask = async (id) => {
        await deleteTrackingTask(id);
        await load();
    };

    const handleUnmark = async () => {
        if (!window.confirm("¿Quitar la marca de adjudicada? Se mantienen compromisos y tareas."))
            return;
        await unmarkTenderAwarded(tenderId);
        onBack();
    };

    if (loading || !data) {
        return (
            <div className="p-12 text-center text-ink-400">
                <Loader2 size={28} className="animate-spin mx-auto mb-3" />
                Cargando seguimiento…
            </div>
        );
    }

    const t = data.tender;
    const completion = computeCompletion(data.commitments);

    return (
        <div className="p-6 md:p-8 space-y-6 max-w-6xl mx-auto" data-testid="tracking-detail">
            <button
                type="button"
                onClick={onBack}
                className="text-sm text-ink-500 hover:text-brand-600 font-semibold flex items-center gap-1"
                data-testid="tracking-back-btn"
            >
                <ChevronLeft size={14} />
                Volver a adjudicadas
            </button>

            {/* Header */}
            <div className="bg-gradient-to-br from-brand-50 via-white to-emerald-50 border border-brand-100 rounded-3xl p-6 shadow-pop">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                    <div>
                        <div className="text-xs font-bold uppercase tracking-[0.18em] text-emerald-700 mb-1 flex items-center gap-1.5">
                            <Trophy size={11} /> Adjudicada
                        </div>
                        <h1 className="font-display text-2xl md:text-3xl font-black tracking-tight text-ink-950">
                            {t.title}
                        </h1>
                        {t.contracting_authority && (
                            <div className="text-sm text-ink-500 mt-1">
                                <Building2 size={13} className="inline mr-1" />
                                {t.contracting_authority}
                            </div>
                        )}
                    </div>
                    <Button
                        onClick={handleUnmark}
                        variant="outline"
                        className="rounded-full border-rose-200 text-rose-700 hover:bg-rose-50 h-9 text-xs"
                        data-testid="unmark-awarded-btn"
                    >
                        Quitar adjudicación
                    </Button>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
                    <Inline label="Importe adjudicado">
                        {t.awarded_amount
                            ? `${Number(t.awarded_amount).toLocaleString("es-ES")} €`
                            : "—"}
                    </Inline>
                    <Inline label="Nº expediente" mono>
                        {t.contract_reference || "—"}
                    </Inline>
                    <Inline label="Inicio">{fmt(t.kickoff_date) || "—"}</Inline>
                    <Inline label="Fin previsto">{fmt(t.contract_end_date) || "—"}</Inline>
                </div>

                {/* Progress bar (commitments completion) */}
                <div className="mt-6">
                    <div className="flex items-center justify-between text-xs font-bold text-ink-500 mb-2 uppercase tracking-wider">
                        <span>Cumplimiento de compromisos</span>
                        <span className="text-brand-700">{completion.percent}%</span>
                    </div>
                    <div className="h-2 rounded-full bg-ink-100 overflow-hidden">
                        <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${completion.percent}%` }}
                            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                            className="h-full bg-gradient-to-r from-brand-600 to-cyan-500"
                        />
                    </div>
                    <div className="text-xs text-ink-500 mt-1">
                        {completion.done} de {completion.total} cumplidos · {completion.inProgress}{" "}
                        en curso · {completion.pending} pendientes
                    </div>
                </div>
            </div>

            {/* Commitments */}
            <Card
                title="Compromisos ofertados"
                icon={Target}
                cta={
                    <div className="flex gap-2">
                        <Button
                            onClick={handleAiExtract}
                            disabled={extracting}
                            variant="outline"
                            className="rounded-full h-9 px-4 text-xs font-bold border-brand-200 text-brand-700"
                            data-testid="ai-extract-commitments-btn"
                        >
                            {extracting ? (
                                <Loader2 size={13} className="animate-spin mr-2" />
                            ) : (
                                <Sparkles size={13} className="mr-2" />
                            )}
                            Extraer con IA
                        </Button>
                        <Button
                            onClick={() => setShowAddCommitment(true)}
                            className="rounded-full bg-brand-600 hover:bg-brand-700 text-white h-9 px-4 text-xs font-bold"
                            data-testid="add-commitment-btn"
                        >
                            <Plus size={13} className="mr-1" />
                            Nuevo
                        </Button>
                    </div>
                }
            >
                {data.commitments.length === 0 ? (
                    <div className="text-sm text-ink-400 italic py-2">
                        Sin compromisos. Pulsa "Extraer con IA" para que Claude lea el análisis
                        del pliego y proponga los típicos (bolsa de horas, garantía, visitas,
                        mejoras…).
                    </div>
                ) : (
                    <div className="space-y-2">
                        {data.commitments.map((c) => (
                            <CommitmentRow
                                key={c.id}
                                c={c}
                                onToggle={() => handleToggleStatus(c)}
                                onEdit={() => setEditing(c)}
                                onDelete={() => handleDeleteCommitment(c.id)}
                            />
                        ))}
                    </div>
                )}
            </Card>

            {/* Tasks */}
            <Card
                title="Tareas y calendario"
                icon={CalendarIcon}
                cta={
                    <Button
                        onClick={() => setShowAddTask(true)}
                        className="rounded-full bg-brand-600 hover:bg-brand-700 text-white h-9 px-4 text-xs font-bold"
                        data-testid="add-task-btn"
                    >
                        <Plus size={13} className="mr-1" />
                        Nueva tarea
                    </Button>
                }
            >
                {data.tasks.length === 0 ? (
                    <div className="text-sm text-ink-400 italic py-2">
                        Sin tareas. Crea una con fecha y se sincronizará con el calendario.
                    </div>
                ) : (
                    <div className="space-y-2">
                        {data.tasks.map((tk) => (
                            <TaskRow
                                key={tk.id}
                                t={tk}
                                onToggle={() => handleToggleTask(tk)}
                                onEdit={() => setTaskEditing(tk)}
                                onDelete={() => handleDeleteTask(tk.id)}
                            />
                        ))}
                    </div>
                )}
            </Card>

            {/* Notes */}
            <Card title="Notas del expediente" icon={FileText}>
                <div className="flex gap-2 mb-3">
                    <Textarea
                        rows={2}
                        value={noteText}
                        onChange={(e) => setNoteText(e.target.value)}
                        placeholder="Reunión, llamada, incidencia…"
                        className="resize-none flex-1"
                        data-testid="note-input"
                    />
                    <Button
                        onClick={handleAddNote}
                        disabled={!noteText.trim()}
                        className="rounded-xl bg-brand-600 hover:bg-brand-700 text-white font-bold h-auto px-4"
                        data-testid="add-note-btn"
                    >
                        <Plus size={14} />
                    </Button>
                </div>
                {data.notes.length === 0 ? (
                    <div className="text-sm text-ink-400 italic">Sin notas aún.</div>
                ) : (
                    <div className="space-y-2">
                        {data.notes.map((n) => (
                            <div
                                key={n.id}
                                className="bg-slate-50 rounded-xl p-3 border border-ink-100 text-sm"
                            >
                                <div className="flex items-center justify-between gap-2 mb-1">
                                    <div className="text-[10px] uppercase tracking-wider font-bold text-ink-400">
                                        {n.user_email || "?"} · {fmtDT(n.created_at)}
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => handleDeleteNote(n.id)}
                                        className="text-ink-400 hover:text-red-600 transition-colors"
                                    >
                                        <Trash2 size={12} />
                                    </button>
                                </div>
                                <div className="text-ink-800 leading-relaxed whitespace-pre-wrap">
                                    {n.text}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </Card>

            {showAddCommitment && (
                <CommitmentDialog
                    onClose={() => setShowAddCommitment(false)}
                    onSubmit={async (body) => {
                        await createCommitment(tenderId, body);
                        setShowAddCommitment(false);
                        await load();
                    }}
                />
            )}
            {editing && (
                <CommitmentDialog
                    initial={editing}
                    onClose={() => setEditing(null)}
                    onSubmit={async (body) => {
                        await updateCommitment(editing.id, body);
                        setEditing(null);
                        await load();
                    }}
                />
            )}
            {showAddTask && (
                <TaskDialog
                    commitments={data.commitments}
                    onClose={() => setShowAddTask(false)}
                    onSubmit={async (body) => {
                        await createTrackingTask(tenderId, body);
                        setShowAddTask(false);
                        await load();
                    }}
                />
            )}
            {taskEditing && (
                <TaskDialog
                    initial={taskEditing}
                    commitments={data.commitments}
                    onClose={() => setTaskEditing(null)}
                    onSubmit={async (body) => {
                        await updateTrackingTask(taskEditing.id, body);
                        setTaskEditing(null);
                        await load();
                    }}
                />
            )}
        </div>
    );
}

function Card({ title, icon: Icon, cta, children }) {
    return (
        <div className="bg-white rounded-3xl border border-ink-100 shadow-pop overflow-hidden">
            <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-ink-100 bg-slate-50/50">
                <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-brand-50 text-brand-600 flex items-center justify-center">
                        <Icon size={16} />
                    </div>
                    <div className="font-display text-lg font-bold text-ink-950">{title}</div>
                </div>
                {cta}
            </div>
            <div className="p-5">{children}</div>
        </div>
    );
}

function CommitmentRow({ c, onToggle, onEdit, onDelete }) {
    const meta = STATUS_META[c.status] || STATUS_META.pending;
    const overdue =
        c.status !== "done" &&
        c.due_date &&
        c.due_date < new Date().toISOString().slice(0, 10);
    const typeMeta = COMMITMENT_TYPES.find((x) => x.value === c.type) || COMMITMENT_TYPES.slice(-1)[0];
    const progress =
        c.target_value && c.target_value > 0
            ? Math.min(100, Math.round(((c.current_value || 0) / c.target_value) * 100))
            : null;
    return (
        <div
            className={`rounded-xl border ${
                overdue ? "border-rose-200 bg-rose-50/40" : "border-ink-100 bg-white"
            } p-4 flex items-start gap-3`}
            data-testid={`commitment-${c.id}`}
        >
            <button
                type="button"
                onClick={onToggle}
                className={`w-9 h-9 rounded-lg shrink-0 flex items-center justify-center transition-colors ${
                    c.status === "done"
                        ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                        : c.status === "in_progress"
                          ? "bg-sky-100 text-sky-700 hover:bg-sky-200"
                          : "bg-ink-100 text-ink-500 hover:bg-ink-200"
                }`}
                title="Click para cambiar estado"
                data-testid={`commitment-toggle-${c.id}`}
            >
                {c.status === "done" ? <CheckCircle2 size={16} /> : <Target size={15} />}
            </button>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span
                        className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md bg-${typeMeta.color}-50 text-${typeMeta.color}-700`}
                    >
                        {typeMeta.label}
                    </span>
                    <span
                        className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md ${meta.color}`}
                    >
                        {meta.label}
                    </span>
                    {c.frequency && (
                        <span className="text-[10px] uppercase tracking-wider text-ink-400 font-bold">
                            {c.frequency}
                        </span>
                    )}
                    {overdue && (
                        <span className="text-[10px] font-bold uppercase tracking-wider text-rose-700 flex items-center gap-0.5">
                            <AlertCircle size={10} />
                            Vencido
                        </span>
                    )}
                </div>
                <div className="font-bold text-ink-950 text-sm">{c.title}</div>
                {c.description && (
                    <div className="text-xs text-ink-500 mt-1 leading-relaxed">
                        {c.description}
                    </div>
                )}
                <div className="flex items-center gap-4 mt-2 text-xs">
                    {c.target_value !== null && c.target_value !== undefined && (
                        <span className="font-mono">
                            <span className="font-bold text-brand-700">
                                {c.current_value || 0}
                            </span>
                            <span className="text-ink-400"> / {c.target_value}</span>
                            <span className="text-ink-500 ml-1">{c.unit}</span>
                        </span>
                    )}
                    {c.due_date && (
                        <span className="text-ink-500">
                            <Clock size={11} className="inline mr-0.5" />
                            {fmt(c.due_date)}
                        </span>
                    )}
                </div>
                {progress !== null && (
                    <div className="mt-2 h-1.5 rounded-full bg-ink-100 overflow-hidden max-w-xs">
                        <div
                            className="h-full bg-gradient-to-r from-brand-600 to-cyan-500 transition-all duration-700"
                            style={{ width: `${progress}%` }}
                        />
                    </div>
                )}
            </div>
            <div className="flex items-center gap-1 shrink-0">
                <button
                    type="button"
                    onClick={onEdit}
                    className="w-8 h-8 rounded-lg text-ink-400 hover:bg-ink-100 flex items-center justify-center"
                    data-testid={`commitment-edit-${c.id}`}
                >
                    <Edit3 size={13} />
                </button>
                <button
                    type="button"
                    onClick={onDelete}
                    className="w-8 h-8 rounded-lg text-ink-400 hover:bg-red-50 hover:text-red-600 flex items-center justify-center"
                    data-testid={`commitment-delete-${c.id}`}
                >
                    <Trash2 size={13} />
                </button>
            </div>
        </div>
    );
}

function TaskRow({ t, onToggle, onEdit, onDelete }) {
    const meta = STATUS_META[t.status] || STATUS_META.pending;
    const overdue =
        t.status !== "done" &&
        t.due_date &&
        t.due_date < new Date().toISOString().slice(0, 10);
    return (
        <div
            className={`rounded-xl border ${
                overdue ? "border-rose-200 bg-rose-50/40" : "border-ink-100 bg-white"
            } p-4 flex items-start gap-3`}
            data-testid={`task-${t.id}`}
        >
            <button
                type="button"
                onClick={onToggle}
                className={`w-9 h-9 rounded-lg shrink-0 flex items-center justify-center transition-colors ${
                    t.status === "done"
                        ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                        : t.status === "in_progress"
                          ? "bg-sky-100 text-sky-700 hover:bg-sky-200"
                          : "bg-ink-100 text-ink-500 hover:bg-ink-200"
                }`}
                title="Click para cambiar estado"
                data-testid={`task-toggle-${t.id}`}
            >
                {t.status === "done" ? <CheckCircle2 size={16} /> : <ListChecks size={15} />}
            </button>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span
                        className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md ${meta.color}`}
                    >
                        {meta.label}
                    </span>
                    {t.due_date && (
                        <span
                            className={`text-[10px] uppercase tracking-wider font-bold ${
                                overdue ? "text-rose-700" : "text-ink-500"
                            }`}
                        >
                            <Clock size={10} className="inline mr-0.5" />
                            {fmt(t.due_date)}
                        </span>
                    )}
                    {t.assignee && (
                        <span className="text-[10px] uppercase tracking-wider text-ink-400 font-bold">
                            {t.assignee}
                        </span>
                    )}
                </div>
                <div className="font-bold text-ink-950 text-sm">{t.title}</div>
                {t.description && (
                    <div className="text-xs text-ink-500 mt-1 leading-relaxed whitespace-pre-wrap">
                        {t.description}
                    </div>
                )}
            </div>
            <div className="flex items-center gap-1 shrink-0">
                <button
                    type="button"
                    onClick={onEdit}
                    className="w-8 h-8 rounded-lg text-ink-400 hover:bg-ink-100 flex items-center justify-center"
                >
                    <Edit3 size={13} />
                </button>
                <button
                    type="button"
                    onClick={onDelete}
                    className="w-8 h-8 rounded-lg text-ink-400 hover:bg-red-50 hover:text-red-600 flex items-center justify-center"
                >
                    <Trash2 size={13} />
                </button>
            </div>
        </div>
    );
}

function CommitmentDialog({ initial, onClose, onSubmit }) {
    const [form, setForm] = useState(
        initial
            ? { ...initial }
            : {
                  type: "bolsa_horas",
                  title: "",
                  description: "",
                  target_value: "",
                  current_value: 0,
                  unit: "h",
                  frequency: "anual",
                  due_date: "",
                  status: "pending",
              },
    );
    const [submitting, setSubmitting] = useState(false);
    const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        if (!form.title.trim()) {
            toast.error("Falta el título");
            return;
        }
        setSubmitting(true);
        try {
            await onSubmit({
                ...form,
                target_value: form.target_value ? parseFloat(form.target_value) : null,
                current_value: form.current_value ? parseFloat(form.current_value) : 0,
            });
            toast.success(initial ? "Compromiso actualizado" : "Compromiso añadido");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-xl rounded-3xl" data-testid="commitment-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display font-black">
                        {initial ? "Editar compromiso" : "Nuevo compromiso"}
                    </DialogTitle>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-4 mt-2">
                    <div className="grid sm:grid-cols-2 gap-3">
                        <div>
                            <Label>Tipo</Label>
                            <select
                                value={form.type}
                                onChange={update("type")}
                                className="w-full rounded-lg border border-ink-200 px-3 h-10 text-sm bg-white"
                            >
                                {COMMITMENT_TYPES.map((c) => (
                                    <option key={c.value} value={c.value}>
                                        {c.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <Label>Estado</Label>
                            <select
                                value={form.status}
                                onChange={update("status")}
                                className="w-full rounded-lg border border-ink-200 px-3 h-10 text-sm bg-white"
                            >
                                {Object.entries(STATUS_META).map(([k, m]) => (
                                    <option key={k} value={k}>
                                        {m.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div>
                        <Label>Título *</Label>
                        <Input
                            value={form.title}
                            onChange={update("title")}
                            placeholder="Bolsa de horas técnico responsable"
                            data-testid="commitment-title"
                        />
                    </div>
                    <div>
                        <Label>Descripción</Label>
                        <Textarea
                            rows={2}
                            value={form.description}
                            onChange={update("description")}
                            className="resize-none"
                        />
                    </div>
                    <div className="grid sm:grid-cols-4 gap-3">
                        <div>
                            <Label>Objetivo</Label>
                            <Input
                                type="number"
                                step="0.01"
                                value={form.target_value || ""}
                                onChange={update("target_value")}
                                className="font-mono"
                            />
                        </div>
                        <div>
                            <Label>Actual</Label>
                            <Input
                                type="number"
                                step="0.01"
                                value={form.current_value || 0}
                                onChange={update("current_value")}
                                className="font-mono"
                            />
                        </div>
                        <div>
                            <Label>Unidad</Label>
                            <Input
                                value={form.unit}
                                onChange={update("unit")}
                                placeholder="h"
                                className="font-mono"
                            />
                        </div>
                        <div>
                            <Label>Frecuencia</Label>
                            <select
                                value={form.frequency}
                                onChange={update("frequency")}
                                className="w-full rounded-lg border border-ink-200 px-3 h-10 text-sm bg-white"
                            >
                                {["único", "mensual", "trimestral", "semestral", "anual"].map(
                                    (f) => (
                                        <option key={f} value={f}>
                                            {f}
                                        </option>
                                    ),
                                )}
                            </select>
                        </div>
                    </div>
                    <div>
                        <Label>Fecha límite</Label>
                        <Input
                            type="date"
                            value={form.due_date || ""}
                            onChange={update("due_date")}
                        />
                    </div>
                    <DialogFooter className="gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={onClose}
                            className="rounded-full"
                        >
                            Cancelar
                        </Button>
                        <Button
                            type="submit"
                            disabled={submitting}
                            className="rounded-full bg-brand-600 hover:bg-brand-700 text-white font-bold"
                            data-testid="commitment-submit"
                        >
                            {submitting ? (
                                <Loader2 size={14} className="animate-spin" />
                            ) : initial ? (
                                "Guardar"
                            ) : (
                                "Añadir"
                            )}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function TaskDialog({ initial, commitments, onClose, onSubmit }) {
    const [form, setForm] = useState(
        initial
            ? { ...initial }
            : {
                  title: "",
                  description: "",
                  due_date: "",
                  assignee: "",
                  status: "pending",
                  commitment_id: "",
              },
    );
    const [submitting, setSubmitting] = useState(false);
    const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        if (!form.title.trim()) {
            toast.error("Falta el título");
            return;
        }
        setSubmitting(true);
        try {
            await onSubmit({ ...form });
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-xl rounded-3xl" data-testid="task-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display font-black">
                        {initial ? "Editar tarea" : "Nueva tarea"}
                    </DialogTitle>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-4 mt-2">
                    <div>
                        <Label>Título *</Label>
                        <Input
                            value={form.title}
                            onChange={update("title")}
                            placeholder="Reunión de seguimiento mensual"
                            data-testid="task-title"
                        />
                    </div>
                    <div>
                        <Label>Descripción</Label>
                        <Textarea
                            rows={2}
                            value={form.description}
                            onChange={update("description")}
                            className="resize-none"
                        />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3">
                        <div>
                            <Label>Fecha</Label>
                            <Input
                                type="date"
                                value={form.due_date || ""}
                                onChange={update("due_date")}
                                data-testid="task-due-date"
                            />
                        </div>
                        <div>
                            <Label>Responsable (email)</Label>
                            <Input
                                value={form.assignee || ""}
                                onChange={update("assignee")}
                                placeholder="empleado@empresa.es"
                            />
                        </div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3">
                        <div>
                            <Label>Estado</Label>
                            <select
                                value={form.status}
                                onChange={update("status")}
                                className="w-full rounded-lg border border-ink-200 px-3 h-10 text-sm bg-white"
                            >
                                {Object.entries(STATUS_META)
                                    .filter(([k]) => k !== "overdue")
                                    .map(([k, m]) => (
                                        <option key={k} value={k}>
                                            {m.label}
                                        </option>
                                    ))}
                            </select>
                        </div>
                        <div>
                            <Label>Compromiso asociado</Label>
                            <select
                                value={form.commitment_id || ""}
                                onChange={update("commitment_id")}
                                className="w-full rounded-lg border border-ink-200 px-3 h-10 text-sm bg-white"
                            >
                                <option value="">— ninguno —</option>
                                {commitments.map((c) => (
                                    <option key={c.id} value={c.id}>
                                        {c.title}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <DialogFooter className="gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={onClose}
                            className="rounded-full"
                        >
                            Cancelar
                        </Button>
                        <Button
                            type="submit"
                            disabled={submitting}
                            className="rounded-full bg-brand-600 hover:bg-brand-700 text-white font-bold"
                            data-testid="task-submit"
                        >
                            {submitting ? (
                                <Loader2 size={14} className="animate-spin" />
                            ) : initial ? (
                                "Guardar"
                            ) : (
                                "Añadir"
                            )}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function Label({ children }) {
    return (
        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-ink-400 mb-1.5">
            {children}
        </div>
    );
}

function Inline({ label, children, mono }) {
    return (
        <div className="bg-white/70 backdrop-blur rounded-xl px-3 py-2 border border-white">
            <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-ink-400">
                {label}
            </div>
            <div
                className={`text-ink-950 font-bold text-sm mt-0.5 truncate ${
                    mono ? "font-mono" : ""
                }`}
            >
                {children}
            </div>
        </div>
    );
}

function computeCompletion(commitments) {
    const total = commitments.length;
    const done = commitments.filter((c) => c.status === "done").length;
    const inProgress = commitments.filter((c) => c.status === "in_progress").length;
    const pending = commitments.filter((c) => c.status === "pending").length;
    const percent = total ? Math.round((done / total) * 100) : 0;
    return { total, done, inProgress, pending, percent };
}

function fmt(iso) {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        return d.toLocaleDateString("es-ES", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    } catch {
        return iso;
    }
}

function fmtDT(iso) {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        return d.toLocaleString("es-ES", {
            day: "2-digit",
            month: "short",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return iso;
    }
}
